import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Book, Church, Shirt, Star, Quote, Play, GraduationCap, Sparkles } from 'lucide-react';
import { siteData } from '../data/mock';
import { VideoCollage } from '../components/VideoCollage';

const HomePage = () => {
  const { ministry, books, clothier, testimonials, homeAnnouncements } = siteData;
  const { newBook, academy: academyAnnouncement } = homeAnnouncements;

  return (
    <div className="min-h-screen">
      {/* ===== NEW BOOK ANNOUNCEMENT (TOP) ===== */}
      <section className="home-book-announce home-book-announce-top" data-testid="home-book-announce">
        <div className="container mx-auto px-4">
          <div className="hba-grid-v2">
            {/* Left — Book mockup */}
            <div className="hba-book-col">
              <img
                src={newBook.cover}
                alt="Behind the Leader — book cover"
                className="hba-book-img"
                data-testid="home-book-cover"
              />
            </div>

            {/* Right — Copy */}
            <div className="hba-copy-col">
              <p className="hba-eyebrow">
                <Sparkles size={14} /> {newBook.eyebrow}
              </p>
              <h2 className="hba-title">{newBook.title}</h2>
              <p className="hba-subtitle">{newBook.subtitle}</p>
              <p className="hba-tagline">&ldquo;{newBook.tagline}&rdquo;</p>
              <p className="hba-desc">{newBook.description}</p>
              <div className="hba-cta-row">
                <a
                  href={newBook.ctaLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hba-cta-primary"
                  data-testid="home-book-cta"
                >
                  <Book size={16} /> {newBook.ctaLabel}
                </a>
                <Link to="/books" className="hba-cta-secondary" data-testid="home-book-learn-more">
                  Learn More <ArrowRight size={14} />
                </Link>
              </div>
            </div>
          </div>

          {/* Trailer — full width below */}
          <div className="hba-trailer-wrap">
            <p className="hba-trailer-label">
              <Play size={12} /> Watch the Trailer
            </p>
            <div className="hba-video-wrap" data-testid="home-book-trailer">
              <iframe
                src={`https://www.youtube.com/embed/${newBook.youtubeId}?rel=0`}
                title="Behind the Leader — Book Trailer"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
              />
            </div>
            <a
              href={newBook.youtubeUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="hba-watch-link"
              data-testid="home-book-watch-link"
            >
              <Play size={14} /> Watch on YouTube
            </a>
          </div>
        </div>
      </section>

      {/* ===== ACADEMY COMING SOON ===== */}
      <Link to={academyAnnouncement.ctaLink} className="home-academy-link" data-testid="home-academy-card">
        <section className="home-academy-banner">
          <div className="container mx-auto px-4">
            <div className="hab-grid">
              <div className="hab-flyer-col">
                <img
                  src={academyAnnouncement.flyer}
                  alt="Secondary Leadership Academy — Coming Soon"
                  className="hab-flyer"
                />
              </div>
              <div className="hab-copy-col">
                <p className="hab-eyebrow">
                  <GraduationCap size={14} /> {academyAnnouncement.eyebrow}
                </p>
                <p className="hab-institute">{academyAnnouncement.institute}</p>
                <h2 className="hab-title">{academyAnnouncement.title}</h2>
                <p className="hab-subtitle">{academyAnnouncement.subtitle}</p>
                <span className="hab-cta">
                  {academyAnnouncement.ctaLabel}
                  <ArrowRight size={16} />
                </span>
              </div>
            </div>
          </div>
        </section>
      </Link>

      {/* ===== WELCOME / LANDING PORTRAIT (moved down) ===== */}
      <VideoCollage />

      {/* Three Pillars Section */}
      <section className="py-20 bg-gray-900">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="heading-2 mb-6">Three Pillars of Purpose</h2>
            <p className="body-large text-gray-300 max-w-2xl mx-auto">
              Discover the three areas where God has called me to serve and create impact in the world.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Ministry Card */}
            <div className="service-card group">
              <div className="flex items-center justify-center w-16 h-16 bg-lime-400 rounded-full mb-6 mx-auto group-hover:scale-110 transition-transform">
                <Church size={32} className="text-black" />
              </div>
              <h3 className="heading-3 text-center mb-4">{ministry.title}</h3>
              <p className="caption text-center mb-4 text-lime-400">{ministry.subtitle}</p>
              <p className="body-small text-center mb-8 text-gray-300">
                {ministry.description}
              </p>
              <div className="text-center">
                <Link to="/ministry" className="btn-secondary">
                  Learn More <ArrowRight size={16} className="ml-2" />
                </Link>
              </div>
            </div>

            {/* Books Card */}
            <div className="service-card group">
              <div className="flex items-center justify-center w-16 h-16 bg-lime-400 rounded-full mb-6 mx-auto group-hover:scale-110 transition-transform">
                <Book size={32} className="text-black" />
              </div>
              <h3 className="heading-3 text-center mb-4">{books.title}</h3>
              <p className="caption text-center mb-4 text-lime-400">{books.subtitle}</p>
              <p className="body-small text-center mb-8 text-gray-300">
                {books.description}
              </p>
              <div className="text-center">
                <Link to="/books" className="btn-secondary">
                  View Books <ArrowRight size={16} className="ml-2" />
                </Link>
              </div>
            </div>

            {/* Clothier Card */}
            <div className="service-card group">
              <div className="flex items-center justify-center w-16 h-16 bg-lime-400 rounded-full mb-6 mx-auto group-hover:scale-110 transition-transform">
                <Shirt size={32} className="text-black" />
              </div>
              <h3 className="heading-3 text-center mb-4">{clothier.title}</h3>
              <p className="caption text-center mb-4 text-lime-400">{clothier.subtitle}</p>
              <p className="body-small text-center mb-8 text-gray-300">
                {clothier.description}
              </p>
              <div className="text-center">
                <Link to="/clothier" className="btn-secondary">
                  Custom Orders <ArrowRight size={16} className="ml-2" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Book Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          {(() => {
            const featured = books.published.find(b => b.id === 1) || books.published[books.published.length - 1];
            return (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                <div>
                  <img 
                    src={featured.cover} 
                    alt={featured.title}
                    className={featured.coverContain
                      ? "w-full max-w-md mx-auto lg:mx-0 object-contain drop-shadow-2xl"
                      : "w-full max-w-md mx-auto lg:mx-0 rounded-lg shadow-2xl"
                    }
                  />
                </div>
                <div>
                  <h2 className="heading-2 mb-6">Featured Book</h2>
                  <h3 className="heading-3 mb-4">{featured.title}</h3>
                  <p className="body-medium mb-6 text-gray-300">
                    {featured.description}
                  </p>
                  {featured.reviews && featured.reviews.length > 0 && (
                    <div className="flex items-center mb-6">
                      <div className="flex">
                        {[...Array(5)].map((_, i) => (
                          <Star key={i} size={20} className="text-lime-400 fill-current" />
                        ))}
                      </div>
                      <span className="ml-2 text-gray-400">5.0 ({featured.reviews.length} reviews)</span>
                    </div>
                  )}
                  <div className="flex flex-col sm:flex-row gap-4">
                    {featured.purchaseLink && featured.purchaseLink !== '#' ? (
                      <a 
                        href={featured.purchaseLink} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="btn-primary"
                      >
                        Buy Now - {featured.price}
                      </a>
                    ) : (
                      <Link to="/books" className="btn-primary">
                        Learn More <ArrowRight size={16} className="ml-2" />
                      </Link>
                    )}
                  </div>
                </div>
              </div>
            );
          })()}
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-gray-900">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="heading-2 mb-6">What People Say</h2>
            <p className="body-large text-gray-300 max-w-2xl mx-auto">
              Hear from those whose lives have been touched by ministry, inspired by books, and styled by custom clothier services.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {testimonials.map((testimonial) => (
              <div key={testimonial.id} className="service-card">
                <Quote size={32} className="text-lime-400 mb-4" />
                <p className="body-small text-gray-300 mb-6 italic">
                  "{testimonial.quote}"
                </p>
                <div className="flex items-center">
                  <img 
                    src={testimonial.image} 
                    alt={testimonial.name}
                    className="w-12 h-12 rounded-full object-cover mr-4"
                  />
                  <div>
                    <h4 className="text-white font-semibold">{testimonial.name}</h4>
                    <p className="caption text-gray-400">{testimonial.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action Section */}
      <section className="py-20">
        <div className="container mx-auto px-4 text-center">
          <h2 className="heading-2 mb-6">Ready to Begin Your Journey?</h2>
          <p className="body-large text-gray-300 mb-12 max-w-2xl mx-auto">
            Whether you're seeking spiritual guidance, inspiring literature, or custom clothing that reflects your unique style, I'm here to serve you with excellence.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="mailto:romero.sumpter@gmail.com?subject=General Inquiry"
              className="btn-primary"
            >
              Get In Touch <ArrowRight size={18} className="ml-2" />
            </a>
            <Link to="/ministry" className="btn-secondary">
              Explore Services <ArrowRight size={18} className="ml-2" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;